import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { DOCX_MIME_TYPE, SPREADSHEET_MIME_TYPES } from '../hooks/useInlineAttachments';

// Shared by the read view (AttachmentList) and the composer's attachment preview.
// P17 (PERF_AUDIT): the preview parser stacks (pdfjs, SheetJS, docx-preview) are
// large and only needed when a user actually previews that file type. Load each
// as a separate async chunk (the `pdf`/`sheet`/`docx` vendor groups in
// vite.config) instead of shipping them in the initial mailbox bundle.
const PdfPreview = lazy(() => import('../components/PdfPreview').then((m) => ({ default: m.PdfPreview })));
const SpreadsheetPreview = lazy(() =>
  import('../components/SpreadsheetPreview').then((m) => ({ default: m.SpreadsheetPreview })),
);
const DocxPreview = lazy(() => import('../components/DocxPreview').then((m) => ({ default: m.DocxPreview })));

function withSuspense(node: ReactNode) {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      }
    >
      {node}
    </Suspense>
  );
}

/** Inline preview for any previewable type; `null` when the type has no renderer. */
export function renderAttachmentPreview(
  attachment: { mimeType?: string | null; filename: string },
  url: string,
) {
  const mimeType = attachment.mimeType?.toLowerCase() ?? '';

  if (mimeType.startsWith('image/')) {
    return (
      <img
        src={url}
        alt={attachment.filename}
        className="max-h-96 w-auto rounded"
        loading="lazy"
        decoding="async"
      />
    );
  }

  if (mimeType === 'application/pdf') {
    return withSuspense(<PdfPreview file={url} filename={attachment.filename} />);
  }

  if (SPREADSHEET_MIME_TYPES.has(mimeType)) {
    return withSuspense(<SpreadsheetPreview url={url} filename={attachment.filename} />);
  }

  if (mimeType === DOCX_MIME_TYPE) {
    return withSuspense(<DocxPreview url={url} filename={attachment.filename} />);
  }

  if (attachment.mimeType === 'text/plain' || attachment.mimeType === 'text/html') {
    return (
      <iframe
        src={url}
        title={attachment.filename}
        className="h-64 w-full rounded border bg-white"
        sandbox=""
      />
    );
  }

  return null;
}

export function formatFileSize(bytes: number): string {
  if (!bytes) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}
